import { Component, OnDestroy, OnInit } from '@angular/core';
import { BasketsFacade } from '@kion/data-access/data-access-baskets';
import { CmsConfig } from '../../config/cms.config';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { combineLatest, interval, Observable, of, Subject } from 'rxjs';
import { Basket, DeliveryMethod } from '@kion/data-models';
import { debounce, map, take } from 'rxjs/operators';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@kion/ui';
import { SubSink } from 'subsink';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
  selector: 'kion-cms-checkout-delivery',
  templateUrl: './checkout-delivery.component.html',
  styleUrls: ['./checkout-delivery.component.scss']
})
export class CheckoutDeliveryComponent implements OnInit, OnDestroy {

  deliveryMethods$: Observable<DeliveryMethod[]>;
  activeDeliveryMethod$: Observable<DeliveryMethod>;
  basket$: Observable<Basket>;

  basketLoaded$: Subject<Basket> = new Subject<Basket>();

  backLink: string;
  forwardLink: string;

  form: FormGroup;

  subSink: SubSink;

  constructor(
    private basketsFacade: BasketsFacade,
    private cmsConfig: CmsConfig,
    private observer: CorrelationObserverService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private router: Router,
    formBuilder: FormBuilder
  ) {
    const baseRoute =  this.cmsConfig.routes?.checkoutBaseRoute;
    this.backLink = '/' + baseRoute + '/' + this.cmsConfig.routes?.checkoutRoutes?.address;
    this.forwardLink = '/' + baseRoute + '/' + this.cmsConfig.routes?.checkoutRoutes?.payment;
    this.form = formBuilder.group(
      {
        note: new FormControl(null, [Validators.maxLength(255)])
      }
    );
    this.subSink = new SubSink();
  }

  ngOnInit(): void {
    this.subSink.sink = this.observer.subscribeToAction(
      this.basketsFacade.createOrFindBasket(),
      () => this.loadDeliveryMethods(window.localStorage.getItem('basketId')),
      () => {
        console.log("error")
      }
    );
    this.deliveryMethods$ = this.basketsFacade.deliveryMethods$;
    this.basketLoaded$.pipe(take(1)).subscribe(
      basket => {
        if (basket.entries.length === 0) {
          this.router.navigate([this.cmsConfig.routes.basketBaseRoute]).then(() =>  { });
        }
      }
    );
    this.basket$ = this.basketsFacade.all$.pipe(
      map(
        (baskets: Basket[]) => {
          const b =  baskets.find(
            (basket: Basket) => basket.id === window.localStorage.getItem('basketId')
          );
          if (b){
            this.basketLoaded$.next(b)
          }
          return b;
        }
      )
    );
    this.activeDeliveryMethod$ = this.basket$.pipe(
      map(
        (basket: Basket) => {
          if (basket) {
            return basket.deliveryMode;
          }
        }
      )
    );
    this.subSink.sink = this.basket$.subscribe(
      (basket: Basket) => {
        if (basket) {
          const note = this.form.get('note');
          if (!note.value) {
          note.patchValue(basket.deliveryNotice);
          }
          this.form.markAsPristine();
          this.form.markAsUntouched();
        }
      }
    );
    this.subSink.sink = this.form.get('note').valueChanges
      .pipe(debounce(() => interval(1000)))
      .subscribe(
        note => {
          if (this.form.touched || this.form.dirty && this.form.valid) {
            this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
              (basket: Basket) => {
                this.subSink.sink = this.observer.subscribeToAction(
                  this.basketsFacade.setDeliveryMethod(basket.id, {... basket.deliveryMode, note: note}),
                  () => {},
                  () => {
                    this.toastService.show(
                      {
                        text: this.translateService.instant('toast.checkout.delivery.note.save.error'),
                        type: 'error'
                      }
                    );
                  }
                )
              }
            );
          }
        }
      )
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  get canProceed$(): Observable<boolean> {
    return this.basket$.pipe(
      map(
        (basket: Basket) => {
          return !!basket.deliveryMode;
        }
      )
    )
  }

  get note(): AbstractControl {
    return this.form.get('note')
  }

  private loadDeliveryMethods(basketId: string): void {
    this.basketsFacade.loadDeliveryMethods(basketId);
  }

  selected$(method): Observable<boolean> {
    return combineLatest(
      [
        of(method),
        this.activeDeliveryMethod$
      ]
    ).pipe(
      map(
        ([selectedMethod, active]) => {
          return selectedMethod === active;
        }
      )
    )
  }

  selectMethod(deliveryMethod: DeliveryMethod) {
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.basketsFacade.setDeliveryMethod(basket.id, deliveryMethod);
      }
    )
  }
}
